import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MapPin, Phone, ArrowUpRight, CheckCircle2, X } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { api, STATIC_MODE } from "@/lib/api";
import { toast } from "sonner";
import { SectionHeader } from "./Services";

export default function Contact({ content }) {
  const c = content?.contact || {};
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Completá nombre, email y mensaje.");
    setLoading(true);
    try {
      if (STATIC_MODE) {
        // Sitio estático en Ferozo: no hay backend propio disponible desde
        // internet, así que el envío lo resuelve un script PHP subido junto
        // con el build (ver deploy_export en server.py, que lo genera con
        // la config SMTP cargada en el panel admin).
        const res = await fetch("/contact.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error("contact.php failed");
      } else {
        await api.post("/public/contact", form);
      }
      setSent(true);
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("No pudimos enviar tu mensaje. Probemos por WhatsApp.");
    } finally { setLoading(false); }
  };
  return (
    <section id="contacto" data-testid={HOME.contact} className="relative py-24 sm:py-28 px-5 sm:px-8 lg:px-12 bg-surface border-y border-line-2">
      <div className="max-w-7xl mx-auto">
        <SectionHeader eyebrow="Contacto" title="Coordinemos una primera charla" sub={c.body || "Sin compromiso. Conocemos tu contexto y te decimos cuál sería el siguiente paso."} />
        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={<a href={`mailto:${c.email}`} className="text-fg hover:text-brand transition-colors">{c.email}</a>} />
            <InfoRow icon={<Phone className="w-4 h-4" />} label="WhatsApp" value={<a href={`https://wa.me/${c.whatsapp}`} target="_blank" rel="noreferrer" className="text-fg hover:text-brand transition-colors">{c.whatsapp_display}</a>} />
            <InfoRow icon={<MapPin className="w-4 h-4" />} label="Ubicación" value={<div className="text-fg"><div>{c.address_line1}</div><div className="text-mute text-[14px]">{c.address_line2}</div></div>} />
            <div className="hairline-strong mt-6" />
            <div className="flex flex-wrap gap-3 pt-2">
              {c.support_url && <a href={c.support_url} target="_blank" rel="noreferrer" className="btn-line inline-flex items-center gap-1.5 px-4 py-2 text-[13px] rounded-sm">Portal de soporte <ArrowUpRight className="w-3.5 h-3.5" /></a>}
              {c.cloud_url && <a href={c.cloud_url} target="_blank" rel="noreferrer" className="btn-line inline-flex items-center gap-1.5 px-4 py-2 text-[13px] rounded-sm">Luva Cloud <ArrowUpRight className="w-3.5 h-3.5" /></a>}
            </div>
          </div>
          <div className="lg:col-span-7">
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-base border border-line p-6 sm:p-8 space-y-5 rounded-sm">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nombre y apellido" name="name" value={form.name} onChange={onChange} required />
                <Field label="Empresa" name="company" value={form.company} onChange={onChange} />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                <Field label="Teléfono / WhatsApp" name="phone" value={form.phone} onChange={onChange} />
              </div>
              <div>
                <label className="block text-mute text-[12px] mb-1.5">¿En qué podemos ayudarte?</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={5} required className="input-base" placeholder="Contános un poco más sobre tu situación actual." />
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button type="submit" disabled={loading} className="btn-solid inline-flex items-center gap-2 px-5 py-3 text-[14px] rounded-sm disabled:opacity-60">
                  {loading ? "Enviando…" : <>Enviar mensaje <ArrowUpRight className="w-3.5 h-3.5" /></>}
                </button>
                <span className="text-mute text-[12px]">Respondemos en menos de 24h hábiles.</span>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
      <SentModal open={sent} onClose={() => setSent(false)} />
    </section>
  );
}

function SentModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center px-5"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2 }}
            className="relative bg-surface border border-line rounded-sm p-8 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 text-mute hover:text-fg">
              <X className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 mx-auto mb-4 border border-line flex items-center justify-center" style={{ borderRadius: 2 }}>
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-fg font-semibold text-[16px] mb-1.5">Mensaje enviado</div>
            <p className="text-mute text-[13px] leading-relaxed">
              Gracias por escribirnos. Te respondemos en menos de 24h hábiles.
            </p>
            <button onClick={onClose} className="btn-line mt-6 px-5 py-2 text-[13px] rounded-sm">
              Cerrar
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 border border-line bg-base text-fg flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="text-mute text-[11px] tracking-wider uppercase">{label}</div>
        <div className="mt-0.5">{value}</div>
      </div>
    </div>
  );
}
function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-mute text-[12px] mb-1.5">{label}</label>
      <input {...props} className="input-base" />
    </div>
  );
}
